/**
 * Announcement Bar Marquee (Running Bar)
 * Erstellt eine unendlich scrollende Announcement Bar
 */

class AnnouncementBarMarquee extends HTMLElement {
  constructor() {
    super();
    this.marqueeTrack = null;
    this.originalContent = null;
    this.resizeObserver = null;
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  init() {
    const marqueeContent = this.querySelector('.marquee-content');
    if (!marqueeContent) return;

    // Original Content speichern
    this.originalContent = marqueeContent.cloneNode(true);

    // Erstelle Marquee Track
    this.marqueeTrack = document.createElement('div');
    this.marqueeTrack.className = 'marquee-track';
    
    // Hole Einstellungen
    const speed = this.dataset.speed || 'medium';
    const direction = this.dataset.direction || 'left';
    const customDuration = this.dataset.customDuration;
    
    this.marqueeTrack.setAttribute('data-speed', speed);
    this.marqueeTrack.setAttribute('data-direction', direction);
    
    // Setze custom duration wenn vorhanden
    if (speed === 'custom' && customDuration) {
      this.marqueeTrack.style.setProperty('--marquee-custom-duration', `${customDuration}s`);
    }

    // Dupliziere Content für nahtlosen Loop
    const clone1 = this.originalContent.cloneNode(true);
    const clone2 = this.originalContent.cloneNode(true);
    
    this.marqueeTrack.appendChild(clone1);
    this.marqueeTrack.appendChild(clone2);

    // Ersetze Original-Content mit Track
    marqueeContent.parentElement.replaceChild(this.marqueeTrack, marqueeContent);

    // Überwache Größenänderungen für responsives Verhalten
    this.setupResizeObserver();

    // Accessibility: Pausiere Animation bei Fokus
    this.setupAccessibility();
  }

  setupResizeObserver() {
    this.resizeObserver = new ResizeObserver(() => {
      this.adjustMarqueeSpeed();
    });
    this.resizeObserver.observe(this);
  }

  adjustMarqueeSpeed() {
    // Kann verwendet werden, um die Geschwindigkeit basierend auf Content-Breite anzupassen
    // Optional: Implementiere dynamische Geschwindigkeitsanpassung
  }

  setupAccessibility() {
    const links = this.querySelectorAll('a');
    links.forEach(link => {
      link.addEventListener('focus', () => {
        this.pauseAnimation();
      });
      link.addEventListener('blur', () => {
        this.resumeAnimation();
      });
    });
  }

  pauseAnimation() {
    if (this.marqueeTrack) {
      this.marqueeTrack.style.animationPlayState = 'paused';
    }
  }

  resumeAnimation() {
    if (this.marqueeTrack) {
      this.marqueeTrack.style.animationPlayState = 'running';
    }
  }
}

customElements.define('announcement-bar-marquee', AnnouncementBarMarquee);


/**
 * Sticky Announcement Bar
 * Macht die Announcement Bar sticky und versteckt sie beim Runterscrollen
 */
class StickyAnnouncementBar extends HTMLElement {
  constructor() {
    super();
    this.lastScrollY = 0;
    this.ticking = false;
    this.hideOnScroll = this.dataset.hideOnScroll === 'true';
    this.resizeObserver = null;
  }

  connectedCallback() {
    // Stelle sicher dass die Section auch die Klasse bekommt
    const section = this.closest('.shopify-section');
    if (section) {
      section.classList.add('announcement-bar--sticky');
    }

    // Set initial header offset based on bar height
    this.updateHeaderOffset();

    // Observe size changes (e.g., text changes, responsive breakpoints)
    this.resizeObserver = new ResizeObserver(() => this.updateHeaderOffset());
    this.resizeObserver.observe(this);

    if (this.hideOnScroll) this.initScrollBehavior();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.handleScroll);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  initScrollBehavior() {
    this.handleScroll = this.handleScroll.bind(this);
    window.addEventListener('scroll', this.handleScroll, { passive: true });
  }

  handleScroll() {
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        this.updateStickyState();
        this.ticking = false;
      });
      this.ticking = true;
    }
  }

  updateStickyState() {
    const currentScrollY = window.scrollY;
    
    if (currentScrollY > this.lastScrollY && currentScrollY > 100) {
      // Scrolling down - hide bar
      this.classList.add('announcement-bar--hidden');
      const section = this.closest('.shopify-section');
      if (section) section.classList.add('announcement-bar--hidden');
      // When hidden, header should move up
      document.documentElement.style.setProperty('--announcement-bar-height', '0px');
    } else {
      // Scrolling up - show bar
      this.classList.remove('announcement-bar--hidden');
      const section = this.closest('.shopify-section');
      if (section) section.classList.remove('announcement-bar--hidden');
      this.updateHeaderOffset();
    }
    
    this.lastScrollY = currentScrollY;
  }

  updateHeaderOffset() {
    // Compute own height and set CSS variable used by header
    const height = this.offsetHeight || 0;
    document.documentElement.style.setProperty('--announcement-bar-height', `${height}px`);
  }
}

customElements.define('sticky-announcement-bar', StickyAnnouncementBar);
